import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

// Import selectors for building API payloads
import { selectRiskDetails, selectControlsForRisk, buildActionPlanData } from '../selectors/residualRiskSelectors';

// Valid residual risk ratings enum
export const RESIDUAL_RISK_RATINGS = ['Negligible', 'Low', 'Moderate', 'Significant', 'High', 'Critical'];

// Valid action plan ratings enum
export const ACTION_PLAN_RATINGS = ['Unsatisfactory', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

// ============================================================================
// Async Thunks - API calls (assessment-centric)
// ============================================================================

/**
 * Generate residual risk assessment using AI
 * Now assessment-centric: keys by assessmentUuid, not modelId
 */
export const generateResidualRisk = createAsyncThunk(
  'residualRisk/generate',
  async ({ assessmentUuid, modelId, riskId }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      // Use assessmentUuid for selector (this was the bug!)
      const riskData = selectRiskDetails(state, assessmentUuid, riskId);
      const controlsData = selectControlsForRisk(state, riskId);

      if (!riskData) {
        throw new Error(`Risk ${riskId} not found in assessment ${assessmentUuid}`);
      }

      const body = { modelId, assessmentUuid, riskId, riskData, controlsData };
      const response = await authFetch('/.netlify/functions/generateResidualRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const json = await response.json();

      // Validate rating is in enum - if invalid, use null (shows as N/A in UI)
      let residual_rating = json.residual_rating;
      if (!RESIDUAL_RISK_RATINGS.includes(residual_rating)) {
        console.error(`[generateResidualRisk] Invalid rating "${residual_rating}" - setting to null (N/A)`);
        residual_rating = null;
      }

      return {
        assessmentUuid,
        riskId,
        residual_rating,
        residual_explanation: json.residual_explanation || '',
      };
    } catch (error) {
      return rejectWithValue({ assessmentUuid, riskId, error: error.message });
    }
  }
);

/**
 * Analyze action plan effectiveness using AI
 * Now assessment-centric
 */
export const analyzeActionPlan = createAsyncThunk(
  'residualRisk/analyzeActionPlan',
  async ({ assessmentUuid, modelId, riskId, riskName }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const actionPlanData = buildActionPlanData(state, riskId);

      const body = { modelId, assessmentUuid, riskId, riskName, actionPlanData };
      const response = await authFetch('/.netlify/functions/analyzeActionPlan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const json = await response.json();

      // Validate action plan rating
      let action_plan_rating = json.action_plan_rating;
      if (!ACTION_PLAN_RATINGS.includes(action_plan_rating)) {
        console.warn(`[analyzeActionPlan] Invalid rating "${action_plan_rating}", defaulting to "Fair"`);
        action_plan_rating = 'Fair';
      }

      return {
        assessmentUuid,
        riskId,
        action_plan_rating,
        action_plan_analysis: json.action_plan_analysis || '',
        action_plan_short_summary: json.action_plan_short_summary || '',
      };
    } catch (error) {
      return rejectWithValue({ assessmentUuid, riskId, error: error.message });
    }
  }
);

/**
 * Save residual risk evaluation to database
 */
export const saveResidualRisk = createAsyncThunk(
  'residualRisk/save',
  async (evaluationData, { rejectWithValue }) => {
    try {
      // Validate required fields
      if (!evaluationData.assessmentUuid) {
        throw new Error('assessmentUuid is required');
      }
      if (!evaluationData.riskId) {
        throw new Error('riskId is required');
      }

      // Validate residual_rating is in enum - reject if invalid (don't silently default)
      if (evaluationData.residual_rating && !RESIDUAL_RISK_RATINGS.includes(evaluationData.residual_rating)) {
        console.error(`[saveResidualRisk] Invalid rating "${evaluationData.residual_rating}" - rejecting save`);
        throw new Error(`Invalid residual_rating: ${evaluationData.residual_rating}`);
      }

      const response = await authFetch('/.netlify/functions/saveResidualRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(evaluationData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return {
        assessmentUuid: evaluationData.assessmentUuid,
        riskId: evaluationData.riskId,
        ...data,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch residual risk evaluations from database
 * Assessment-centric: keys by assessmentUuid
 */
export const fetchResidualRisks = createAsyncThunk(
  'residualRisk/fetch',
  async ({ assessmentUuid, modelId }, { rejectWithValue }) => {
    try {
      console.log('[fetchResidualRisks] Fetching for assessmentUuid:', assessmentUuid);

      const response = await authFetch('/.netlify/functions/fetchResidualRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('[fetchResidualRisks] Received', result.data?.rows?.length || 0, 'rows');

      return {
        assessmentUuid,
        risks: result.data?.rows || [],
      };
    } catch (error) {
      return rejectWithValue({ assessmentUuid, error: error.message });
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

/**
 * State Shape (assessment-centric):
 * {
 *   byAssessment: {
 *     [assessmentUuid]: {
 *       [riskId]: {
 *         residual_rating: string,
 *         residual_explanation: string,
 *         action_plan_rating: string,
 *         action_plan_analysis: string,
 *         action_plan_short_summary: string,
 *         loading: boolean,
 *         actionPlanLoading: boolean,
 *         error: string | null,
 *         actionPlanError: string | null,
 *         overridden: { ... } | null,
 *         saved: boolean
 *       }
 *     },
 *     loading: boolean,
 *     error: string | null
 *   }
 * },
 *   saving: boolean,
 *   saveError: string | null
 * }
 */

const initialState = {
  byAssessment: {},
  saving: false,
  saveError: null,
};

const residualRiskSlice = createSlice({
  name: 'residualRiskRTK',
  initialState,
  reducers: {
    // Manual override of residual risk rating
    updateResidualRiskLocal: (state, action) => {
      const { assessmentUuid, riskId, overrideData } = action.payload;
      if (!state.byAssessment[assessmentUuid]) {
        state.byAssessment[assessmentUuid] = {};
      }
      if (!state.byAssessment[assessmentUuid][riskId]) {
        state.byAssessment[assessmentUuid][riskId] = {};
      }
      state.byAssessment[assessmentUuid][riskId].overridden = {
        residual_rating: overrideData.residual_rating,
        residual_explanation: overrideData.residual_explanation,
        action_plan_rating: overrideData.action_plan_rating,
      };
    },

    // Clear residual risk data for an assessment
    clearResidualRisks: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },
  },
  extraReducers: (builder) => {
    // Generate residual risk
    builder
      .addCase(generateResidualRisk.pending, (state, action) => {
        const { assessmentUuid, riskId } = action.meta.arg;
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = {};
        }
        if (!state.byAssessment[assessmentUuid][riskId]) {
          state.byAssessment[assessmentUuid][riskId] = {};
        }
        state.byAssessment[assessmentUuid][riskId].loading = true;
        state.byAssessment[assessmentUuid][riskId].error = null;
      })
      .addCase(generateResidualRisk.fulfilled, (state, action) => {
        const { assessmentUuid, riskId, residual_rating, residual_explanation } = action.payload;
        const existing = state.byAssessment[assessmentUuid]?.[riskId] || {};
        state.byAssessment[assessmentUuid][riskId] = {
          ...existing,
          residual_rating,
          residual_explanation,
          loading: false,
          error: null,
        };
      })
      .addCase(generateResidualRisk.rejected, (state, action) => {
        const { assessmentUuid, riskId, error } = action.payload || action.meta.arg;
        if (state.byAssessment[assessmentUuid]?.[riskId]) {
          state.byAssessment[assessmentUuid][riskId].loading = false;
          state.byAssessment[assessmentUuid][riskId].error = error || action.error.message;
        }
      });

    // Analyze action plan
    builder
      .addCase(analyzeActionPlan.pending, (state, action) => {
        const { assessmentUuid, riskId } = action.meta.arg;
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = {};
        }
        if (!state.byAssessment[assessmentUuid][riskId]) {
          state.byAssessment[assessmentUuid][riskId] = {};
        }
        state.byAssessment[assessmentUuid][riskId].actionPlanLoading = true;
        state.byAssessment[assessmentUuid][riskId].actionPlanError = null;
      })
      .addCase(analyzeActionPlan.fulfilled, (state, action) => {
        const { assessmentUuid, riskId, action_plan_rating, action_plan_analysis, action_plan_short_summary } = action.payload;
        const existing = state.byAssessment[assessmentUuid]?.[riskId] || {};
        state.byAssessment[assessmentUuid][riskId] = {
          ...existing,
          action_plan_rating,
          action_plan_analysis,
          action_plan_short_summary,
          actionPlanLoading: false,
          actionPlanError: null,
        };
      })
      .addCase(analyzeActionPlan.rejected, (state, action) => {
        const { assessmentUuid, riskId, error } = action.payload || action.meta.arg;
        if (state.byAssessment[assessmentUuid]?.[riskId]) {
          state.byAssessment[assessmentUuid][riskId].actionPlanLoading = false;
          state.byAssessment[assessmentUuid][riskId].actionPlanError = error || action.error.message;
        }
      });

    // Save residual risk
    builder
      .addCase(saveResidualRisk.pending, (state, action) => {
        state.saving = true;
        state.saveError = null;

        // Optimistic update: immediately show the new values in UI
        const { assessmentUuid, riskId, residual_rating, residual_explanation, action_plan_rating, action_plan_analysis } = action.meta.arg;

        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = {};
        }
        if (!state.byAssessment[assessmentUuid][riskId]) {
          state.byAssessment[assessmentUuid][riskId] = {};
        }

        const existing = state.byAssessment[assessmentUuid][riskId];

        // Update fields that were provided
        if (residual_rating !== undefined) {
          existing.residual_rating = residual_rating;
        }
        if (residual_explanation !== undefined) {
          existing.residual_explanation = residual_explanation;
        }
        if (action_plan_rating !== undefined) {
          existing.action_plan_rating = action_plan_rating;
          existing.actionPlanRating = action_plan_rating; // Keep both for compatibility
        }
        if (action_plan_analysis !== undefined) {
          existing.action_plan_analysis = action_plan_analysis;
          existing.actionPlanAnalysis = action_plan_analysis; // Keep both for compatibility
        }
      })
      .addCase(saveResidualRisk.fulfilled, (state, action) => {
        const { assessmentUuid, riskId } = action.payload;
        state.saving = false;
        if (state.byAssessment[assessmentUuid]?.[riskId]) {
          state.byAssessment[assessmentUuid][riskId].saved = true;
        }
      })
      .addCase(saveResidualRisk.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || action.error.message;
      });

    // Fetch residual risks
    builder
      .addCase(fetchResidualRisks.pending, (state, action) => {
        const { assessmentUuid } = action.meta.arg;
        if (!state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid] = {};
        }
        state.byAssessment[assessmentUuid].loading = true;
        state.byAssessment[assessmentUuid].error = null;
      })
      .addCase(fetchResidualRisks.fulfilled, (state, action) => {
        const { assessmentUuid, risks } = action.payload;
        // Convert array to object keyed by risk_id
        const riskData = {};
        if (Array.isArray(risks)) {
          risks.forEach((row) => {
            riskData[row.risk_id] = {
              residual_rating: row.residual_rating,
              residual_explanation: row.residual_explanation,
              action_plan_rating: row.action_plan_rating,
              action_plan_analysis: row.action_plan_analysis,
              action_plan_short_summary: row.action_plan_short_summary,
              priority: row.priority,
              saved: true,
            };
          });
        }
        state.byAssessment[assessmentUuid] = {
          ...state.byAssessment[assessmentUuid],
          ...riskData,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchResidualRisks.rejected, (state, action) => {
        const { assessmentUuid, error } = action.payload || action.meta.arg;
        if (state.byAssessment[assessmentUuid]) {
          state.byAssessment[assessmentUuid].loading = false;
          state.byAssessment[assessmentUuid].error = error || action.error.message;
        }
      });
  },
});

export const { updateResidualRiskLocal, clearResidualRisks } = residualRiskSlice.actions;
export default residualRiskSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

/**
 * Get residual risk data for a specific risk in an assessment
 */
export const selectResidualRisk = (state, assessmentUuid, riskId) =>
  state.residualRiskRTK?.byAssessment?.[assessmentUuid]?.[riskId] || null;

/**
 * Get all residual risks for an assessment
 */
export const selectResidualRisksForAssessment = (state, assessmentUuid) =>
  state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {};

/**
 * Get the effective residual rating (override if present, else generated)
 */
export const selectEffectiveResidualRating = (state, assessmentUuid, riskId) => {
  const data = selectResidualRisk(state, assessmentUuid, riskId);
  if (!data) return null;
  return data.overridden?.residual_rating || data.residual_rating;
};
